import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveVoicesPost,
  createVoicesPost,
  deleteVoicesPost,
  draftVoicesPost,
  editScheduledVoicesPost,
  getVoicesCalendar,
  getVoicesPost,
  getVoicesPosts,
  moveVoicesPost,
  rejectVoicesPost,
  retryVoicesPost,
  stopVoicesPost,
  submitVoicesPost,
  updateVoicesPost,
} from "../Api/voicesPosts";
import { VoicesPostState } from "../Enums/voices";
import {
  VoicesPost,
  VoicesPostContent,
  VoicesPostOrder,
  VoicesPostPayload,
} from "../Models/voicesPosts";
import { VoicesCalendarFilters, VoicesCalendarPost } from "../Models/voicesCalendar";

const HANDOVER_POLL_MS = 3000;

const waitsForBuffer = (post: VoicesPost) =>
  post.state === VoicesPostState.SCHEDULED && !post.in_buffer;

export const useVoicesPosts = (order: VoicesPostOrder, campaignId?: number) => {
  return useQuery<VoicesPost[], Error>({
    queryKey: ["voicesPosts", campaignId ?? "all", order],
    queryFn: () => getVoicesPosts(order, campaignId),
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      query.state.data?.some(waitsForBuffer) ? HANDOVER_POLL_MS : false,
  });
};

const usePostMutation = <TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["voicesPosts"] });
      queryClient.invalidateQueries({ queryKey: ["voicesToday"] });
      queryClient.invalidateQueries({ queryKey: ["voicesCalendar"] });
      queryClient.invalidateQueries({ queryKey: ["voicesPost"] });
      queryClient.invalidateQueries({ queryKey: ["voicesTopics"] });
    },
  });
};

export const useCreateVoicesPost = () =>
  usePostMutation((data: VoicesPostPayload) => createVoicesPost(data));

export const useUpdateVoicesPost = () =>
  usePostMutation((variables: { postId: number; data: VoicesPostContent }) =>
    updateVoicesPost(variables.postId, variables.data),
  );

export const useDeleteVoicesPost = () =>
  usePostMutation((postId: number) => deleteVoicesPost(postId));

export const useDraftVoicesPost = () =>
  usePostMutation((postId: number) => draftVoicesPost(postId));

export const useSubmitVoicesPost = () =>
  usePostMutation((postId: number) => submitVoicesPost(postId));

export const useStopVoicesPost = () =>
  usePostMutation((variables: { postId: number; note: string }) =>
    stopVoicesPost(variables.postId, variables.note),
  );

export const useRetryVoicesPost = () =>
  usePostMutation((postId: number) => retryVoicesPost(postId));

export const useApproveVoicesPost = () =>
  usePostMutation((variables: { postId: number; changes: Partial<VoicesPostContent> }) =>
    approveVoicesPost(variables.postId, variables.changes),
  );

export const useRejectVoicesPost = () =>
  usePostMutation((variables: { postId: number; note: string }) =>
    rejectVoicesPost(variables.postId, variables.note),
  );

export const useEditScheduledVoicesPost = () =>
  usePostMutation((variables: { postId: number; changes: Partial<VoicesPostContent> }) =>
    editScheduledVoicesPost(variables.postId, variables.changes),
  );

export const useVoicesPost = (postId: number | null) => {
  return useQuery<VoicesPost, Error>({
    queryKey: ["voicesPost", postId],
    queryFn: () => getVoicesPost(postId as number),
    enabled: postId !== null,
  });
};

export const useVoicesCalendar = (from: string, to: string, filters: VoicesCalendarFilters) => {
  return useQuery<VoicesCalendarPost[], Error>({
    queryKey: ["voicesCalendar", from, to, filters],
    queryFn: () => getVoicesCalendar(from, to, filters),
  });
};

export const useMoveVoicesPost = () =>
  usePostMutation((variables: { postId: number; scheduledAt: string }) =>
    moveVoicesPost(variables.postId, variables.scheduledAt),
  );
